/**
 * TEMPORARY local-testing mock: grants the logged-in user extra modules, for modules the
 * backend cannot assign yet (e.g. 124 Recargas Automáticas before its backend is merged).
 *
 * Off unless `REACT_APP_MOCK_MODULES` is set, and never in a production build. Format:
 * comma-separated `<module>:<READ|WRITE>`, e.g. `REACT_APP_MOCK_MODULES=124:WRITE`.
 * Put it only in a local, gitignored env file (`.env.onboard.dev`).
 *
 * To remove: delete this file and its two call sites (`isAuthorized` in `helpers/auth.js`,
 * `HasPermission`), both marked `mockModules`.
 */

interface ModuleLevel {
  module: string;
  type: string;
}

const parseMockModules = (): ModuleLevel[] => {
  if (process.env.NODE_ENV === 'production') return [];
  const raw = process.env.REACT_APP_MOCK_MODULES;
  if (!raw) return [];
  return raw
    .split(',')
    .map((entry) => entry.trim())
    .filter(Boolean)
    .map((entry) => {
      const [module, type = 'WRITE'] = entry.split(':');
      return { module: module.trim(), type: type.trim().toUpperCase() };
    });
};

const MOCK_MODULES = parseMockModules();

export const hasMockModules = MOCK_MODULES.length > 0;

/**
 * The user's module keys plus the mocked ones.
 * @param {string[]} modules the user's real module keys
 * @return {string[]} the module keys with the mocked ones added
 */
export const withMockModules = (modules: string[] = []): string[] => {
  if (!hasMockModules) return modules;
  const mocked = MOCK_MODULES.map(({ module }) => module);
  return [...modules.filter((module) => !mocked.includes(module)), ...mocked];
};

/**
 * The user's module levels with the mocked ones replacing any real level for the same module.
 * @param {ModuleLevel[]} levels the user's real levels
 * @return {ModuleLevel[]} the levels with the mocked ones applied
 */
export const withMockLevels = (levels: ModuleLevel[] = []): ModuleLevel[] => {
  if (!hasMockModules) return levels;
  const mocked = MOCK_MODULES.map(({ module }) => module);
  return [
    ...levels.filter(({ module }) => !mocked.includes(module)),
    ...MOCK_MODULES,
  ];
};
